import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ShippingRateUpdateInput } from "@/modules/shipping/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircleIcon } from "lucide-react";
import { useRateQuery, useUpdateRate, useZonesQuery } from "../api/queries";
import { RateFormTour, useRateFormTour } from "../tour";
import { RateForm } from "../ui/RateForm";

export function RateEditPage() {
  const nav = useNavigate({ from: "/app/shipping/rates/$id/edit" });
  const { id } = useParams({ from: "/app/shipping/rates/$id/edit" });
  const { data, ...result } = useRateQuery(id);
  const updateRate = useUpdateRate(id);
  const zonesQuery = useZonesQuery();
  const zones = zonesQuery.data ?? [];
  const { run, handleJoyrideCallback, startTour } = useRateFormTour();
  const rate = data;
  const rateLabel =
    rate && typeof rate.name === "object" && rate.name !== null
      ? ((rate.name as { lo?: string })?.lo ??
        (rate.name as { en?: string })?.en ??
        (rate as { slug?: string })?.slug)
      : ((rate as { name?: string })?.name ??
        (rate as { slug?: string })?.slug ??
        id ??
        "ແກ້ໄຂ");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ອັດຕາຄ່າສົ່ງ", to: "/app/shipping/rates" },
            { label: rateLabel },
          ]}
        />
      </Header>
      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂອັດຕາຄ່າສົ່ງ{" "}
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="ml-2 size-6"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircleIcon className="h-4 w-4" />
              </Button>
            </h2>
            <p className="text-muted-foreground">ປັບລາຍລະອຽດຂອງອັດຕາຄ່າສົ່ງ.</p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/shipping/rates" } as any)}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>
        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດອັດຕາຄ່າສົ່ງ"
          description="ກຳລັງດຶງລາຍລະອຽດອັດຕາຄ່າສົ່ງ"
          variant="fullscreen"
        >
          {!rate ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <RateForm
                zones={zones}
                initialValues={{
                  zoneId: rate.zoneId,
                  weightMinGrams: rate.weightMinGrams,
                  weightMaxGrams: rate.weightMaxGrams,
                  amount: rate.amount,
                }}
                onSubmit={async (values: ShippingRateUpdateInput) => {
                  await updateRate.mutateAsync(values);
                  nav({ to: "/app/shipping/rates" } as any);
                }}
                onCancel={() => nav({ to: "/app/shipping/rates" } as any)}
                submitting={updateRate.isPending}
              />
            </div>
          )}
        </QueryState>
        <RateFormTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
